import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from './cn';

// ─────────────────────────────────────────────────────────────────────────────
// pageActions — the GLOBAL bottom action bar (Lisa, 2026-07-19: "Any buttons
// on that page I want a copy in the bar — so the bar needs to change with
// the buttons according to the page you are on, and role.").
//
// A page's primary action buttons get a COPY rendered in one fixed bar at
// the bottom of the app shell — the originals on the page stay exactly
// where they are. Navigating (or switching tabs within one page, e.g.
// TournamentSetup's tool-strip) changes the bar's contents automatically,
// because registration lives on the mount lifecycle of the component that
// owns the button.
//
// ── The two registration flavours ───────────────────────────────────────────
//   usePageActions(actions: PageAction[] | null)
//     Plain {label, onClick} buttons, rendered by the bar itself. Pass an
//     array of PageAction (each with a globally-unique `id` — used as the
//     bar's React key; prefix it with your component's name so two callers
//     can never collide even if they end up mounted together, e.g.
//     'discipline-issue', 'org-members-add'). Pass `null` (or an empty
//     array) to register nothing / clear a previous registration.
//
//   usePageActionNode(id: string, node: ReactNode | null)
//     One arbitrary ReactNode registered under a single explicit `id` — for
//     a compound control that already bundles its own buttons/state (e.g.
//     CompetitionLifecycleControls: status pill + Activate + End + Advance).
//     Mount the SAME element a second time here rather than re-deriving its
//     behaviour as plain actions — one implementation, two render sites, no
//     duplicated wiring. Pass `node: null` to unregister.
//
// ── Contract for callers ────────────────────────────────────────────────────
//   - Both flavours UNREGISTER automatically on unmount — a page change (or
//     a conditional un-render, e.g. a tab switching away) empties that
//     caller's slice of the bar with no extra cleanup code.
//   - The bar renders the UNION of every currently-registered caller, in
//     REGISTRATION ORDER (first-mounted, first-shown) — plain actions and
//     nodes interleave in whatever order their owning components mounted.
//     Multiple components on one page each keep their own slot; none of
//     them can stomp on another's entry.
//   - Role-awareness needs NO special support here: register from a
//     component that already renders role-gated (an early `return null`, an
//     `if (!canX) return null` above the JSX, a conditional block) by
//     passing `null` down that same branch — when the gate fails, that
//     caller's bar entry is simply absent. Never re-derive a role check in
//     this file; it has none of its own.
//   - Re-registration on dep change should not churn: memoize the `actions`
//     array / `node` element you pass in (useMemo, or a stable literal) so
//     the effect below only re-fires when the CONTENT actually changes, not
//     on every unrelated re-render. An unmemoized value still works
//     correctly (React batches the unregister+re-register within one
//     commit, so there's no visible flicker) — memoizing just avoids
//     needless registry churn.
//   - StrictMode double-invoke safe: registration is idempotent per slot key
//     (keyed by call-site — last write wins), so a dev-only double-mount
//     (mount → cleanup → remount) never leaves a stale or duplicate entry.
//
// ── Bar mechanics + `--activation-bar-h` ownership ──────────────────────────
// PageActionsProvider (mounted in Layout.tsx, wrapping the routed content)
// owns the registry AND the `--activation-bar-h` CSS var globally: whenever
// at least one entry is registered, ActivationBarSpacer (also mounted in
// Layout, wrapping `children`) sets `--activation-bar-h: 4rem` plus a
// matching `paddingBottom` so the var cascades to every descendant
// regardless of nesting depth — SaveBar's `bottom-[var(--activation-bar-h,0px)]`
// stacks above the bar on ANY page that has one now, not just Setup, and a
// page's own trailing content clears the fixed bar at the bottom of scroll.
// Empty registry → no inline style at all → SaveBar's `0px` default,
// unchanged from before this system existed.
// ─────────────────────────────────────────────────────────────────────────────

export type PageAction = {
  /** Globally-unique — used as the bar's React key. Prefix with your
   * component's name so two callers can never collide. */
  id: string;
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  disabled?: boolean;
  /** Visual weight — defaults to 'ghost' (the bar is a shortcut rail, not a
   * page full of primary CTAs; opt into 'primary' for the one action that
   * genuinely deserves the emphasis). */
  variant?: 'primary' | 'ghost';
  title?: string;
};

type Entry =
  | { kind: 'actions'; actions: PageAction[] }
  | { kind: 'node'; node: ReactNode };

type PageActionsApi = {
  entries: Map<string, Entry>;
  setEntry: (key: string, entry: Entry | null) => void;
};

const PageActionsContext = createContext<PageActionsApi | null>(null);

export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<Map<string, Entry>>(() => new Map());

  const setEntry = useCallback((key: string, entry: Entry | null) => {
    setEntries(prev => {
      const already = prev.get(key);
      // No-op guard: unregistering a slot that was never (or no longer)
      // registered shouldn't trigger a re-render of every bar consumer.
      if (!entry && !already) return prev;
      const next = new Map(prev);
      if (entry) next.set(key, entry);
      else next.delete(key);
      return next;
    });
  }, []);

  const api = useMemo<PageActionsApi>(() => ({ entries, setEntry }), [entries, setEntry]);

  return <PageActionsContext.Provider value={api}>{children}</PageActionsContext.Provider>;
}

function usePageActionsApi(): PageActionsApi {
  const ctx = useContext(PageActionsContext);
  if (!ctx) {
    throw new Error(
      'usePageActions/usePageActionNode/PageActionBar must be used within <PageActionsProvider> (mounted in Layout.tsx around the routed content).',
    );
  }
  return ctx;
}

let slotCounter = 0;
/** One stable key per component instance — set once (lazy ref init), never
 * changes across re-renders, so this caller always updates/clears the SAME
 * registry slot regardless of how many times it re-registers. */
function useStableSlotKey(): string {
  const ref = useRef<string | null>(null);
  if (ref.current === null) ref.current = `pa-slot-${++slotCounter}`;
  return ref.current;
}

/** Register a set of plain {label, onClick} actions. Pass `null` (or an
 * empty array) to register nothing / clear. See this file's header comment
 * for the full contract. */
export function usePageActions(actions: PageAction[] | null): void {
  const { setEntry } = usePageActionsApi();
  const key = useStableSlotKey();
  useEffect(() => {
    setEntry(key, actions && actions.length > 0 ? { kind: 'actions', actions } : null);
    return () => setEntry(key, null);
  }, [setEntry, key, actions]);
}

/** Register one arbitrary ReactNode under an explicit, caller-chosen `id`.
 * Pass `node: null` to unregister. See this file's header comment for the
 * full contract. */
export function usePageActionNode(id: string, node: ReactNode | null): void {
  const { setEntry } = usePageActionsApi();
  useEffect(() => {
    setEntry(id, node != null ? { kind: 'node', node } : null);
    return () => setEntry(id, null);
  }, [setEntry, id, node]);
}

// ── Bar rendering ────────────────────────────────────────────────────────────

function ActionButton({ action }: { action: PageAction }) {
  const Icon = action.icon;
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      title={action.title}
      // Explicit high-contrast treatment, not the page-level btn-* classes:
      // on the bar's SOLID grey plate the quiet ghost style vanished (Lisa,
      // 2026-07-19). Primary = forge accent; default = dark chip, visible
      // border, white text — reads clearly against bg-iron-900.
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 font-display text-xs font-black uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        action.variant === 'primary'
          ? 'bg-accent-500 text-[#0a0a0c] hover:bg-accent-400'
          : 'border border-iron-500 bg-iron-950 text-white hover:border-accent-500 hover:text-accent-400',
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {action.label}
    </button>
  );
}

/** Renders ONLY when at least one action/node is registered — same
 * "occupies dead space never" convention SaveBar already uses. Same visual
 * language as the old Setup-only bar it replaces: fixed inset-x-0 bottom-0
 * z-20, border-t iron, backdrop-blur dark bg, px-4 py-2.5, pr-28 (reserves
 * the global HelpFab's bottom-right corner — components/HelpFab.tsx, fixed
 * z-40, draws on top), safe-area bottom padding, inner mx-auto max-w-7xl,
 * buttons in a flex-wrap row. */
export function PageActionBar() {
  const { entries } = usePageActionsApi();
  if (entries.size === 0) return null;

  return (
    <div
      className={cn(
        // Visibility pass (Lisa, 2026-07-19 evening): SOLID grey plate — the
        // translucent blur read as "hard to see"; buttons get their own
        // contrast treatment below so they stand out from the plate.
        // md:left clears the app sidebar AND (when Setup's tool rail is
        // mounted) the rail — both published as CSS vars — so the bar's
        // first buttons are never cut off underneath them.
        'fixed left-0 right-0 bottom-0 z-20 border-t border-iron-700 bg-iron-900 px-4 py-2.5 pr-28',
        'md:left-[calc(var(--app-sidebar-w,0px)+var(--tool-rail-w,0px))]',
        'pb-[calc(0.625rem+env(safe-area-inset-bottom))]',
      )}
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
        {Array.from(entries.entries()).map(([key, entry]) =>
          entry.kind === 'actions' ? (
            entry.actions.map(action => <ActionButton key={action.id} action={action} />)
          ) : (
            <Fragment key={key}>{entry.node}</Fragment>
          ),
        )}
      </div>
    </div>
  );
}

/** Wraps the routed content, owning the `--activation-bar-h` var + matching
 * paddingBottom (see this file's header comment) — mount once in Layout.tsx
 * in place of the plain `<div className="mx-auto max-w-7xl">{children}</div>`
 * wrapper it replaces. `className` is forwarded so Layout keeps its existing
 * outer classes unchanged. */
export function ActivationBarSpacer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { entries } = usePageActionsApi();
  const hasBar = entries.size > 0;
  return (
    <div
      className={className}
      style={hasBar ? ({ '--activation-bar-h': '4rem', paddingBottom: 'var(--activation-bar-h)' } as CSSProperties) : undefined}
    >
      {children}
    </div>
  );
}
